/**
 * SurfaceInsetHandler — Object Mode tool for inset+extrude on a picked face.
 *
 * Two-stage modal interaction:
 *
 *   1. Stage `null` (armed): click a face → resolve a coplanar region, walk
 *      the region boundary, simplify collinear runs, project into basis-UV
 *      with the cap centroid as origin. Advances to stage `edit`.
 *
 *   2. Stage `edit`: `SurfaceInsetGizmo` exposes both the inset ring and
 *      the extrude arrow simultaneously. The user can grab either — ring
 *      to inset first, then arrow to extrude; or arrow directly to extrude
 *      with the full face boundary as footprint. Releasing the arrow
 *      confirms the operator. This handler does not touch pointer events
 *      during stage `edit`; it only owns hover, cancel paths, and the
 *      external-disarm subscribe.
 *
 * Cancel paths (Esc, blur, pointercancel, sub-epsilon height) leave the
 * scene untouched. Zero inset is intentional — the footprint becomes the
 * picked face boundary itself.
 */
import { useThree } from "@react-three/fiber";
import { useEffect } from "react";
import { useStore } from "../app/store.ts";
import { selectSurfaceToolOfKind } from "../app/surfaceToolSelectors.ts";
import {
  extractFaceRegionBoundaryLoop,
  simplifyCoplanarLoop,
} from "../modeling/coplanarBoundaryLoop.ts";
import { coplanarSurfaceRegionFromHit } from "../modeling/surfaceSelection.ts";
import {
  clearSurfaceSelectionHighlight,
  setSurfaceSelectionHighlightHover,
  setSurfaceSelectionHighlightSelection,
} from "./surfaceSelectionHighlight.ts";
import { resolveSurfaceSelectionAtPointer } from "./surfaceSelectionInput.ts";

function transformLocalToWorld(
  localPositions: Float32Array,
  worldMatrix: readonly number[],
): Float32Array {
  const out = new Float32Array(localPositions.length);
  const n = localPositions.length / 3;
  for (let i = 0; i < n; i += 1) {
    const x = localPositions[i * 3]!;
    const y = localPositions[i * 3 + 1]!;
    const z = localPositions[i * 3 + 2]!;
    out[i * 3] =
      worldMatrix[0]! * x +
      worldMatrix[4]! * y +
      worldMatrix[8]! * z +
      worldMatrix[12]!;
    out[i * 3 + 1] =
      worldMatrix[1]! * x +
      worldMatrix[5]! * y +
      worldMatrix[9]! * z +
      worldMatrix[13]!;
    out[i * 3 + 2] =
      worldMatrix[2]! * x +
      worldMatrix[6]! * y +
      worldMatrix[10]! * z +
      worldMatrix[14]!;
  }
  return out;
}

export function SurfaceInsetHandler() {
  const { gl } = useThree();

  useEffect(() => {
    const canvas = gl.domElement;
    let prevCursor = "";

    function isArmed(): boolean {
      const s = useStore.getState();
      return (
        s.activeOperator === "surface_inset" && s.workspaceMode === "object"
      );
    }

    function reset() {
      // Clear the slot entirely on reset — keeping the symmetry with
      // SurfaceBoxHandler / SurfacePolyHandler where leaving an empty
      // `{ kind, stage: null, ... }` shell would confuse the next
      // pickFace's slot-init logic and break the alignment grid.
      const st = useStore.getState();
      if (selectSurfaceToolOfKind(st, "inset")) {
        st.setSurfaceTool(null);
      }
      clearSurfaceSelectionHighlight();
      if (prevCursor !== "") {
        canvas.style.cursor = prevCursor;
        prevCursor = "";
      }
    }

    function pickFace(e: PointerEvent): boolean {
      const result = resolveSurfaceSelectionAtPointer(
        canvas,
        e.clientX,
        e.clientY,
      );
      if (!result) return false;
      const hit = result.hit;
      const mesh = hit.face?.mesh;
      if (!mesh) return false;

      const region = coplanarSurfaceRegionFromHit(hit);
      if (!region) return false;

      const worldPositions = transformLocalToWorld(
        mesh.positions,
        mesh.worldMatrix,
      );

      const rawLoop = extractFaceRegionBoundaryLoop(
        mesh.topology,
        new Set(region.faceIndices),
        worldPositions,
        result.basis,
      );
      if (!rawLoop || rawLoop.length < 3) return false;
      const simplified = simplifyCoplanarLoop(rawLoop);
      if (simplified.length < 3) return false;

      // Re-center the loop on its centroid so the new SceneObject's local
      // origin lines up with the picked-region center.
      let cu = 0;
      let cv = 0;
      for (const [u, v] of simplified) {
        cu += u;
        cv += v;
      }
      cu /= simplified.length;
      cv /= simplified.length;
      const centeredLoop = simplified.map(
        ([u, v]) => [u - cu, v - cv] as readonly [number, number],
      );
      const centroidWorld: [number, number, number] = [
        result.basis.origin[0] +
          cu * result.basis.tangent[0] +
          cv * result.basis.bitangent[0],
        result.basis.origin[1] +
          cu * result.basis.tangent[1] +
          cv * result.basis.bitangent[1],
        result.basis.origin[2] +
          cu * result.basis.tangent[2] +
          cv * result.basis.bitangent[2],
      ];

      useStore.getState().setSurfaceTool({
        kind: "inset",
        stage: "edit",
        draft: {
          basis: result.basis,
          hostObjectId: result.hostObjectId,
          boundaryLoop: centeredLoop,
          centroid: centroidWorld,
          insetDistance: 0,
          height: 0,
          profileTarget: { kind: "face" },
        },
        active: false,
      });
      setSurfaceSelectionHighlightSelection([result.hit]);
      setSurfaceSelectionHighlightHover(null);
      return true;
    }

    function updateHover(e: PointerEvent) {
      if (!isArmed()) return;
      const stage =
        selectSurfaceToolOfKind(useStore.getState(), "inset")?.stage ?? null;
      // Only highlight prospective surface picks while the modal is armed
      // and no face is yet locked in (stage = null). Once `edit` begins,
      // the gizmo owns visual feedback.
      if (stage !== null) {
        setSurfaceSelectionHighlightHover(null);
        return;
      }
      const result = resolveSurfaceSelectionAtPointer(
        canvas,
        e.clientX,
        e.clientY,
      );
      setSurfaceSelectionHighlightHover(result?.hit ?? null);
    }

    function onPointerDown(e: PointerEvent) {
      if (e.button !== 0) return;
      if (!isArmed()) return;
      const tool = selectSurfaceToolOfKind(useStore.getState(), "inset");
      if (tool && tool.stage !== null) return; // gizmo owns subsequent stages
      if (!pickFace(e)) return;
      e.preventDefault();
      e.stopPropagation();
      prevCursor = canvas.style.cursor;
      canvas.style.cursor = "crosshair";
    }

    function onPointerMove(e: PointerEvent) {
      updateHover(e);
    }

    function onPointerLeave() {
      setSurfaceSelectionHighlightHover(null);
    }

    function onWindowBlur() {
      reset();
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        const stage =
          selectSurfaceToolOfKind(useStore.getState(), "inset")?.stage ?? null;
        if (stage === null && !isArmed()) return;
        e.preventDefault();
        if (stage === null) {
          useStore.getState().armToolOperator(null);
          return;
        }
        // Disarm symmetric with Box/Cylinder/Poly: the operator-disarm
        // handler in `armToolOperator` clears the surfaceTool slot for us.
        useStore.getState().armToolOperator(null);
      }
    }

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerleave", onPointerLeave);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("blur", onWindowBlur);

    const unsubscribe = useStore.subscribe((s, prev) => {
      if (
        prev.activeOperator === "surface_inset" &&
        s.activeOperator !== "surface_inset"
      ) {
        reset();
      }
    });

    return () => {
      reset();
      unsubscribe();
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerleave", onPointerLeave);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("blur", onWindowBlur);
    };
  }, [gl]);

  return null;
}
